import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ComparisonResult {
  id: string;
  timestamp: Date;
  method: string;
  endpoint: string;
  nodeStatus: number;
  phpStatus: number;
  isMatch: boolean;
  nodeDuration: number;
  phpDuration: number;
  diff?: {
    statusMismatch?: boolean;
    structureDiff?: string[];
    typeDiff?: string[];
    valueDiff?: string[];
  };
  nodeResponse?: any;
  phpResponse?: any;
  phpError?: string;
}

export interface ComparisonStats {
  total: number;
  passed: number;
  failed: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiComparisonService {
  private resultsSubject = new BehaviorSubject<ComparisonResult[]>([]);
  public results$: Observable<ComparisonResult[]> = this.resultsSubject.asObservable();

  private maxResults = 100; // Limiter pour éviter memory leak

  constructor() {}

  /**
   * Ajoute un résultat de comparaison
   */
  addResult(result: ComparisonResult): void {
    const current = this.resultsSubject.value;
    const updated = [result, ...current].slice(0, this.maxResults);
    this.resultsSubject.next(updated);
  }

  /**
   * Retourne les résultats actuels
   */
  getResults(): ComparisonResult[] {
    return this.resultsSubject.value;
  }

  /**
   * Vide les résultats
   */
  clearResults(): void {
    this.resultsSubject.next([]);
  }

  /**
   * Retourne les statistiques de comparaison
   */
  getStats(): ComparisonStats {
    const results = this.resultsSubject.value;
    return {
      total: results.length,
      passed: results.filter(r => r.isMatch).length,
      failed: results.filter(r => !r.isMatch).length
    };
  }

  /**
   * Compare deux réponses et détecte les différences
   */
  compareResponses(nodeResponse: any, phpResponse: any): { isMatch: boolean; diff?: ComparisonResult['diff'] } {
    const diff: ComparisonResult['diff'] = {};
    let isMatch = true;

    // Comparaison des structures JSON
    const structureDiff = this.compareStructure(nodeResponse, phpResponse);
    if (structureDiff.length > 0) {
      diff.structureDiff = structureDiff;
      isMatch = false;
    }

    // Comparaison des types
    const typeDiff = this.compareTypes(nodeResponse, phpResponse);
    if (typeDiff.length > 0) {
      diff.typeDiff = typeDiff;
      isMatch = false;
    }

    // Comparaison des valeurs critiques (totalCount, etc.)
    const valueDiff = this.compareCriticalValues(nodeResponse, phpResponse);
    if (valueDiff.length > 0) {
      diff.valueDiff = valueDiff;
      isMatch = false;
    }

    return { isMatch, diff: isMatch ? undefined : diff };
  }

  /**
   * Compare la structure des deux objets (clés présentes)
   */
  private compareStructure(node: any, php: any, path = ''): string[] {
    const diffs: string[] = [];

    if (typeof node !== typeof php) {
      return [`${path || 'root'}: type differs (node: ${typeof node}, php: ${typeof php})`];
    }

    if (Array.isArray(node) && Array.isArray(php)) {
      // Pour les tableaux, on compare le premier élément si présent
      if (node.length > 0 && php.length > 0) {
        diffs.push(...this.compareStructure(node[0], php[0], `${path}[0]`));
      }
    } else if (typeof node === 'object' && node !== null && php !== null) {
      const nodeKeys = Object.keys(node);
      const phpKeys = Object.keys(php);

      const missingInPhp = nodeKeys.filter(k => !phpKeys.includes(k));
      const extraInPhp = phpKeys.filter(k => !nodeKeys.includes(k));

      missingInPhp.forEach(k => diffs.push(`${path ? path + '.' : ''}${k}: missing in PHP`));
      extraInPhp.forEach(k => diffs.push(`${path ? path + '.' : ''}${k}: extra in PHP`));

      // Récursion sur les clés communes
      const commonKeys = nodeKeys.filter(k => phpKeys.includes(k));
      commonKeys.forEach(k => {
        diffs.push(...this.compareStructure(node[k], php[k], `${path ? path + '.' : ''}${k}`));
      });
    }

    return diffs.slice(0, 10); // Limiter le nombre de diffs
  }

  /**
   * Compare les types des valeurs
   */
  private compareTypes(node: any, php: any, path = ''): string[] {
    const diffs: string[] = [];

    if (typeof node !== typeof php) {
      diffs.push(`${path || 'root'}: ${typeof node} vs ${typeof php}`);
    } else if (Array.isArray(node) !== Array.isArray(php)) {
      diffs.push(`${path || 'root'}: array vs object`);
    } else if (typeof node === 'object' && node !== null && php !== null && !Array.isArray(node)) {
      Object.keys(node).forEach(k => {
        if (php.hasOwnProperty(k)) {
          diffs.push(...this.compareTypes(node[k], php[k], `${path ? path + '.' : ''}${k}`));
        }
      });
    }

    return diffs.slice(0, 10);
  }

  /**
   * Compare les valeurs critiques (totalCount, id, etc.)
   */
  private compareCriticalValues(node: any, php: any): string[] {
    const diffs: string[] = [];

    // Comparer totalCount pour les réponses paginées
    if (node?.totalCount !== undefined && php?.totalCount !== undefined) {
      if (node.totalCount !== php.totalCount) {
        diffs.push(`totalCount: ${node.totalCount} vs ${php.totalCount}`);
      }
    }

    // Comparer count pour les tableaux
    if (Array.isArray(node) && Array.isArray(php)) {
      if (node.length !== php.length) {
        diffs.push(`array length: ${node.length} vs ${php.length}`);
      }
    }

    return diffs;
  }

  /**
   * Génère un ID unique pour le résultat
   */
  generateId(): string {
    return `cmp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
